import axios from "axios";
import { ApiResponse, UUID } from "@/types";
import { isNil, isEmpty } from "lodash";
import axiosInstance from "@/helpers/axiosInstance";

const REMOVED_PUZZLE_REQUIRED_STATUS = 404;
const REMOVED_PUZZLE_KEYWORDS = ["puzzle", "removed"] as const;
/**
 * Detects whether an API error represents a removed puzzle response.
 *
 * This intentionally avoids exact message matching so minor backend wording
 * or punctuation changes do not break frontend detection.
 */
export const isRemovedPuzzleError = (error: unknown): boolean => {
    if (!axios.isAxiosError(error)) {
        return false;
    }
    const response = error.response;
    const message = typeof response?.data?.message === "string" ? response.data.message.toLowerCase() : "";
    return (
        response?.status === REMOVED_PUZZLE_REQUIRED_STATUS &&
        REMOVED_PUZZLE_KEYWORDS.every((keyword) => message.includes(keyword))
    );
};

/**
 * Puzzle statistics response from the backend API
 */
export interface PuzzleStatsResponse {
    averageFirstGuess: number | null;
    averageScore: number | null;
    highestGuess: number | null;
    lowestGuess: number | null;
    totalUsers: number;
    guessCount: number;
    repliesCount: number;
}

/**
 * Form format for listing agent data (from admin UI)
 */
export interface ListingAgentFormData {
    agentFirstName?: string;
    agentLastName?: string;
    agentEmail?: string;
    agentPhoneNumber?: string;
    agentBrokerage?: string;
    agentPrimaryLink?: string;
    agentImageLink?: string;
    agentInstagram?: string;
    agentTiktok?: string;
    agentTwitter?: string;
    agentFacebook?: string;
    agentYoutube?: string;
    agentMlsId?: string;
}

/** One image item for API: existing (keep URL) or new (file sent in multipart "images" in order). */
export type ImageItemApi = { type: "existing"; url: string } | { type: "new" };

/**
 * Form format for property data (from admin UI)
 */
export interface PropertyDataFormData {
    mlsId?: string;
    address?: string;
    streetAddress?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    neighborhood?: string;
    publicRemarks?: string;
    listPrice?: number | string;
    bedrooms?: number | string;
    bathrooms?: number | string;
    propertyType?: string;
    squareFootage?: number | string;
    parkingSpots?: number | string;
    parkingType?: string;
    yearBuilt?: number | string;
    exteriorFeatures?: string;
    interiorFeatures?: string;
    heating?: string;
    cooling?: string;
    laundryFeatures?: string;
    lotDimensions?: string;
    listingId?: string;
    lat?: number | string;
    lng?: number | string;
    externalPropertyLink?: string;
    imageUrls?: string | string[]; // Can be string (newline-separated) or array (legacy)
    /** Ordered mix of existing URLs and new uploads; when set, "images" multipart = only new files in order. */
    imageItems?: ImageItemApi[];
}

export const getSalesData = async (propertyId: UUID): Promise<ApiResponse<any>> => {
    return (await axios.get(`/api/properties/${propertyId}/sales_data`)).data;
};

export const getDailyGuestProperties = async ({
    year,
    week,
}: {
    year: number;
    week: number;
}): Promise<ApiResponse<any>> => {
    const query = new URLSearchParams({ year: String(year), week: String(week) }).toString();
    return (await axios.get(`/api/properties/daily-guest-properties?${query}`)).data;
};

export const getLiveVariantDailyProperty = async ({
    variantLink = undefined,
    playDate = undefined,
    requestingUserId = undefined,
}: {
    variantLink?: string | undefined;
    playDate?: string | undefined;
    requestingUserId?: string | undefined;
}) => {
    const query = new URLSearchParams({
        ...(variantLink && { variantLink }),
        ...(playDate && { playDate }),
        ...(requestingUserId && { requestingUserId }),
    }).toString();
    return await axios.get(`/api/playable-properties/daily?${query}`);
};

export const getNearestProperty = async ({
    location = "",
    limit = 0,
    guessedProperties = [] as string[],
}: {
    location?: string;
    limit?: number;
    guessedProperties?: string[];
}) => {
    const query = new URLSearchParams({
        location,
        limit: String(limit),
        guessed_properties: guessedProperties as any,
    }).toString();
    return await axios.get(`/api/properties/nearest?${query}`);
};

export const resolveLink = async ({
    shortLink = undefined,
    playDate = undefined,
    longLink = undefined,
}: {
    shortLink?: string | undefined;
    playDate?: string | undefined;
    longLink?: string | undefined;
}) => {
    const query = new URLSearchParams({
        ...(shortLink && { shortLink }),
        ...(playDate && { playDate }),
        ...(longLink && { longLink }),
    }).toString();
    return await axios.get(`/api/playable-properties/resolve?${query}`);
};

export const getCoordinates = async (address: string): Promise<any> => {
    if (!address) throw new Error("Address is required.");
    const query = new URLSearchParams({ address }).toString();
    const response = await axios.get(`/api/properties/coordinates?${query}`);
    return response.data.coordinates;
};

export const getPlayableProperties = async ({
    minPlayDate,
    maxPlayDate,
    page,
    limit,
    includeFuture,
}: {
    minPlayDate?: string;
    maxPlayDate?: string;
    page?: number;
    limit?: number;
    includeFuture?: boolean;
} = {}): Promise<any> => {
    const query = new URLSearchParams({
        ...(minPlayDate && { minPlayDate }),
        ...(maxPlayDate && { maxPlayDate }),
        ...(page && { page: String(page) }),
        ...(limit && { limit: String(limit) }),
        ...(includeFuture !== undefined && { includeFuture: String(includeFuture) }),
    }).toString();
    return await axios.get(`/api/playable-properties${query ? `?${query}` : ""}`);
};

export const getListingFeedProperties = async ({
    page = "",
    limit = "",
    userId,
    feedType = "",
    userFeedPreferenceId = "",
}: {
    page?: string;
    limit?: string;
    userId?: string;
    feedType?: string;
    userFeedPreferenceId?: string;
}) => {
    if (feedType === "trending") {
        const query = new URLSearchParams({
            page,
            limit,
            ...(!isNil(userId) && !isEmpty(userId) && { userId }),
        }).toString();

        return await axios.get(`/api/feeds/social/trending?${query}`);
    }
    const query = new URLSearchParams({
        page,
        limit,
        ...(!isNil(userId) && !isEmpty(userId) && ({ userId } as any)),
        ...(!isNil(userFeedPreferenceId) && !isEmpty(userFeedPreferenceId) && ({ userFeedPreferenceId } as any)),
    }).toString();
    return await axios.get(`/api/feeds/social/following?${query}`);
};

export const getPuzzleFeed = async ({
    page = 1,
    limit = 10,
    userId,
    requestingUserId,
    slc,
}: {
    page?: number;
    limit?: number;
    userId?: string;
    requestingUserId?: string;
    slc?: string | null;
}) => {
    const query = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        ...(!isNil(userId) && !isEmpty(userId) && { userId }),
        ...(!isNil(requestingUserId) && !isEmpty(requestingUserId) && { requestingUserId }),
        ...(!isNil(slc) && !isEmpty(slc) && { slc }),
    }).toString();
    return await axios.get(`/api/feeds/puzzle?${query}`);
};

export const getListingPageProperty = async ({ property_id }: { property_id: string }) => {
    return await axios.get(`/api/properties/${property_id}`);
};

/**
 * Get comprehensive puzzle statistics for a playable property.
 * Returns community stats including averages, extremes, user counts, and engagement metrics.
 *
 * @param {string} playablePropertyId - The playable property ID to fetch stats for
 * @returns {Promise<PuzzleStatsResponse>} Complete puzzle statistics including:
 *   - averageFirstGuess: Average of all users' first guesses
 *   - averageScore: Average score (number of guesses to solve)
 *   - highestGuess: Highest guess amount across all users
 *   - lowestGuess: Lowest guess amount across all users
 *   - totalUsers: Total number of users who made guesses
 *   - guessCount: Total number of distinct users who guessed
 *   - repliesCount: Total number of non-deleted comments/replies
 */
export const getPropertyPuzzleStats = async (playablePropertyId: string): Promise<PuzzleStatsResponse> => {
    const { data } = await axios.get(`/api/properties/${playablePropertyId}/puzzle/stats`);
    return data;
};

export const getListingPropertyGuessStats = async ({ propertyId }: { propertyId: string }) => {
    return await axios.get(`/api/user-guesses/${propertyId}/social/guess-statistics`);
};

export const getRandomArchivedProperty = async () => {
    return await axios.get("/api/playable-properties/random/archive");
};

export const getPropertyByLinkCode = async ({ property_link_code }: { property_link_code: string }) => {
    return await axios.get(`/api/properties/link/${property_link_code}`);
};

export const createPlayablePropertyComment = async ({
    playablePropertyId,
    content,
    userId,
    parentId,
}: {
    playablePropertyId: string;
    content: string;
    userId: UUID;
    parentId?: string | null;
}) => {
    if (isNil(userId) || isEmpty(userId)) {
        throw new Error("userId is required to create a playable property comment.");
    }

    const payload: Record<string, unknown> = {
        body: content,
        userId,
        ...(!isNil(parentId) && !isEmpty(parentId) && { parentCommentId: parentId }),
    };
    return (await axiosInstance.post(`/api/playable-properties/${playablePropertyId}/comments`, payload)).data;
};

const numericPropertyFields: (keyof PropertyDataFormData)[] = [
    "listPrice",
    "bedrooms",
    "bathrooms",
    "squareFootage",
    "parkingSpots",
    "yearBuilt",
    "lat",
    "lng",
];

const parseNumericValue = (value?: number | string): number | undefined => {
    if (value == null) {
        return undefined;
    }

    if (typeof value === "number") {
        return Number.isFinite(value) ? value : undefined;
    }

    const trimmed = value.trim();
    if (isEmpty(trimmed)) {
        return undefined;
    }

    // Only allow an optional leading hyphen, digits, optional single decimal point with digits
    if (!/^[-]?\d+(\.\d+)?$/.test(trimmed)) {
        return undefined;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
};

const removeEmptyFields = <T extends Record<string, any>>(data: T | undefined): T | undefined => {
    if (!data) return undefined;

    const cleaned = { ...data };
    Object.keys(cleaned).forEach((key) => {
        const value = cleaned[key];
        if (
            value === undefined ||
            value === null ||
            (typeof value === "string" && value.trim() === "") ||
            (Array.isArray(value) && value.length === 0)
        ) {
            delete cleaned[key];
        }
    });

    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

const formatListingAgent = (listingAgent?: ListingAgentFormData) => {
    const cleanedAgent = removeEmptyFields(listingAgent);
    if (!cleanedAgent) return undefined;

    const formatted = {
        firstName: cleanedAgent.agentFirstName,
        lastName: cleanedAgent.agentLastName,
        email: cleanedAgent.agentEmail,
        phoneNumber: cleanedAgent.agentPhoneNumber,
        brokerage: cleanedAgent.agentBrokerage,
        link: cleanedAgent.agentPrimaryLink,
        imageLink: cleanedAgent.agentImageLink,
        instagram: cleanedAgent.agentInstagram,
        tiktok: cleanedAgent.agentTiktok,
        twitter: cleanedAgent.agentTwitter,
        facebook: cleanedAgent.agentFacebook,
        youtube: cleanedAgent.agentYoutube,
        mlsId: cleanedAgent.agentMlsId,
    };

    return removeEmptyFields(formatted);
};

const formatPropertyData = (propertyData?: PropertyDataFormData) => {
    if (!propertyData) return undefined;

    const formatted: Record<string, any> = {};

    // Required fields that must be present for validation (even if empty)
    const requiredStringFields = ["streetAddress", "city", "state", "zipCode", "neighborhood"];
    const requiredNumericFields = ["listPrice"];

    Object.entries(propertyData).forEach(([key, value]) => {
        if (value === undefined || value === null) {
            // Skip undefined/null values
            return;
        }

        if (typeof value === "string") {
            const trimmed = value.trim();
            // Only include non-empty strings
            if (trimmed.length > 0) {
                formatted[key] = trimmed;
            }
        } else {
            formatted[key] = value;
        }
    });

    if (!formatted.streetAddress && formatted.address) {
        formatted.streetAddress = formatted.address;
    }
    delete formatted.address;

    if (typeof propertyData.imageUrls === "string") {
        const urls = propertyData.imageUrls
            .split(/\r?\n/)
            .map((url) => url.trim())
            .filter((url) => url.length > 0);
        if (urls.length > 0) {
            formatted.imageUrls = urls;
        }
    } else if (Array.isArray(propertyData.imageUrls)) {
        const urls = propertyData.imageUrls.filter((url) => typeof url === "string" && url.trim().length > 0);
        if (urls.length > 0) {
            formatted.imageUrls = urls;
        }
    }

    // When using imageItems (existing + new in order), do not send legacy imageUrls
    if (Array.isArray(propertyData.imageItems) && propertyData.imageItems.length > 0) {
        formatted.imageItems = propertyData.imageItems;
        delete formatted.imageUrls;
    }

    numericPropertyFields.forEach((field) => {
        const value = propertyData[field];
        // Skip if value is an array (shouldn't happen for numeric fields, but TypeScript needs this check)
        if (Array.isArray(value)) return;
        const parsed = parseNumericValue(value);
        if (parsed !== undefined) {
            formatted[field] = parsed;
        }
    });

    const cleaned = removeEmptyFields(formatted) || {};

    // Ensure required fields are always present with defaults (only add if missing)
    requiredStringFields.forEach((field) => {
        if (!(field in cleaned)) {
            cleaned[field] = "";
        }
    });
    requiredNumericFields.forEach((field) => {
        if (!(field in cleaned)) {
            cleaned[field] = 0;
        }
    });

    return cleaned;
};

const parseOptionalBoolean = (value?: boolean | string): boolean | undefined => {
    if (value === undefined || value === null || value === "") return undefined;
    if (typeof value === "boolean") return value;

    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) return true;
    if (["false", "0", "no", "n"].includes(normalized)) return false;
    return undefined;
};

const parseOptionalNumber = (value?: number | string): number | undefined => parseNumericValue(value);

export const setDailyPlayableProperty = async (
    payload: {
        playDate: string;
        mlsId?: string;
        image_index?: number;
        url?: string;
        listingAgent?: ListingAgentFormData;
        propertyData?: PropertyDataFormData;
        emailPuzzleSubject?: string;
        emailPuzzleImage?: number;
        emailPuzzleBody?: string;
        featureAgent?: boolean | string;
        longLink?: string;
        sendDailyEmail?: boolean | string;
        sendClaimAccountLink?: boolean | string;
        sendPasswordReset?: boolean | string;
        creatorUserId?: UUID;
        postBody?: string | null;
    },
    images?: File[],
    agentImage?: File,
) => {
    const {
        playDate,
        mlsId,
        image_index,
        url,
        listingAgent,
        propertyData,
        emailPuzzleSubject,
        emailPuzzleImage,
        emailPuzzleBody,
        featureAgent,
        longLink,
        sendDailyEmail,
        sendClaimAccountLink,
        sendPasswordReset,
        creatorUserId,
        postBody,
    } = payload;

    const formattedListingAgent = formatListingAgent(listingAgent);
    const formattedPropertyData = formatPropertyData(propertyData);
    const parsedFeatureAgent = parseOptionalBoolean(featureAgent);
    const parsedSendDailyEmail = parseOptionalBoolean(sendDailyEmail);
    const parsedSendClaimAccountLink = parseOptionalBoolean(sendClaimAccountLink);
    const parsedSendPasswordReset = parseOptionalBoolean(sendPasswordReset);
    const parsedEmailImage = parseOptionalNumber(emailPuzzleImage);
    const parsedImageIndex = parseOptionalNumber(image_index);

    const useMultipart = (images && images.length > 0) || agentImage;

    if (useMultipart) {
        const formData = new FormData();
        formData.append("playDate", playDate);
        formData.append("imageIndex", String(parsedImageIndex || 1));
        if (mlsId) formData.append("listingId", mlsId);
        if (url) formData.append("url", url);
        if (longLink) formData.append("longLink", longLink);
        if (emailPuzzleSubject) formData.append("emailPuzzleSubject", emailPuzzleSubject);
        if (parsedEmailImage !== undefined) formData.append("emailPuzzleImage", String(parsedEmailImage));
        if (emailPuzzleBody) formData.append("emailPuzzleBody", emailPuzzleBody);
        if (parsedFeatureAgent !== undefined) formData.append("featureAgent", String(parsedFeatureAgent));
        if (parsedSendDailyEmail !== undefined) {
            formData.append("sendDailyEmail", String(parsedSendDailyEmail));
        }
        if (parsedSendClaimAccountLink !== undefined) {
            formData.append("sendClaimAccountLink", String(parsedSendClaimAccountLink));
        }
        if (parsedSendPasswordReset !== undefined) {
            formData.append("sendPasswordReset", String(parsedSendPasswordReset));
        }
        if (creatorUserId) formData.append("creatorUserId", creatorUserId);
        if (postBody != null) {
            formData.append("body", postBody);
        }
        if (formattedListingAgent && Object.keys(formattedListingAgent).length > 0) {
            formData.append("listingAgent", JSON.stringify(formattedListingAgent));
        }
        if (formattedPropertyData && Object.keys(formattedPropertyData).length > 0) {
            formData.append("propertyData", JSON.stringify(formattedPropertyData));
        }
        if (images?.length) {
            images.forEach((file) => formData.append("images", file));
        }
        if (agentImage) {
            formData.append("agentImage", agentImage);
        }

        return await axiosInstance.post("/api/playable-properties", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    }

    return await axiosInstance.post("/api/playable-properties", {
        ...(mlsId && { listingId: mlsId }),
        ...(url && { url }),
        playDate,
        imageIndex: parsedImageIndex || 1,
        ...(formattedListingAgent && { listingAgent: formattedListingAgent }),
        ...(formattedPropertyData && { propertyData: formattedPropertyData }),
        ...(emailPuzzleSubject && { emailPuzzleSubject }),
        ...(parsedEmailImage !== undefined && { emailPuzzleImage: parsedEmailImage }),
        ...(emailPuzzleBody && { emailPuzzleBody }),
        ...(parsedFeatureAgent !== undefined && { featureAgent: parsedFeatureAgent }),
        ...(parsedSendDailyEmail !== undefined && { sendDailyEmail: parsedSendDailyEmail }),
        ...(parsedSendClaimAccountLink !== undefined && { sendClaimAccountLink: parsedSendClaimAccountLink }),
        ...(parsedSendPasswordReset !== undefined && { sendPasswordReset: parsedSendPasswordReset }),
        ...(longLink && { longLink }),
        ...(creatorUserId && { creatorUserId }),
        ...(postBody !== undefined && { body: postBody }),
    });
};

export const getPlayablePropertyBySlug = async (slug: string) => {
    const parts = slug.split("-");
    const shortLink = parts[parts.length - 1]; // Last part is the short link

    const response = await axios.get(`/api/playable-properties/resolve?shortLink=${shortLink}`);
    // Resolve API returns an array; admin edit mode expects a single object
    const data = Array.isArray(response.data) ? (response.data[0] ?? null) : response.data;
    return { ...response, data };
};

export const uploadAgentImage = async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append("agentImage", file);
    const res = await axios.post<{ url: string }>("/api/agents/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
};

export const deletePlayableProperty = async (id: string) => {
    return await axiosInstance.delete(`/api/playable-properties/${id}`);
};

export interface UpdatePlayablePropertyPayload {
    playDate?: string;
    mlsId?: string;
    url?: string;
    imageIndex?: number | string;
    listingAgent?: ListingAgentFormData;
    propertyData?: PropertyDataFormData;
    emailPuzzleSubject?: string;
    emailPuzzleImage?: number | string;
    emailPuzzleBody?: string;
    featureAgent?: boolean | string;
    longLink?: string;
    sendDailyEmail?: boolean | string;
    sendClaimAccountLink?: boolean | string;
    sendPasswordReset?: boolean | string;
    /** UUID to re-attribute the puzzle; null resets the creator to the PriceMe Admin user; undefined leaves it untouched */
    creatorUserId?: UUID | null;
    postBody?: string | null;
}

export const updatePlayableProperty = async (
    id: string,
    payload: UpdatePlayablePropertyPayload,
    images?: File[],
    agentImage?: File,
) => {
    const formattedListingAgent = formatListingAgent(payload.listingAgent);
    const formattedPropertyData = formatPropertyData(payload.propertyData);
    const parsedFeatureAgent = parseOptionalBoolean(payload.featureAgent);
    const parsedSendDailyEmail = parseOptionalBoolean(payload.sendDailyEmail);
    const parsedSendClaimAccountLink = parseOptionalBoolean(payload.sendClaimAccountLink);
    const parsedSendPasswordReset = parseOptionalBoolean(payload.sendPasswordReset);
    const parsedEmailImage = parseOptionalNumber(payload.emailPuzzleImage);
    const parsedImageIndex = parseOptionalNumber(payload.imageIndex);

    const useMultipart = (images && images.length > 0) || !!agentImage || formattedPropertyData?.imageItems?.length > 0;

    if (useMultipart) {
        const formData = new FormData();
        if (payload.mlsId) formData.append("listingId", payload.mlsId);
        if (payload.url) formData.append("url", payload.url);
        if (payload.playDate) formData.append("playDate", payload.playDate);
        if (parsedImageIndex !== undefined) formData.append("imageIndex", String(parsedImageIndex));
        if (payload.emailPuzzleSubject) formData.append("emailPuzzleSubject", payload.emailPuzzleSubject);
        if (parsedEmailImage !== undefined) formData.append("emailPuzzleImage", String(parsedEmailImage));
        if (payload.emailPuzzleBody) formData.append("emailPuzzleBody", payload.emailPuzzleBody);
        if (parsedFeatureAgent !== undefined) formData.append("featureAgent", String(parsedFeatureAgent));
        if (parsedSendDailyEmail !== undefined) {
            formData.append("sendDailyEmail", String(parsedSendDailyEmail));
        }
        if (parsedSendClaimAccountLink !== undefined) {
            formData.append("sendClaimAccountLink", String(parsedSendClaimAccountLink));
        }
        if (parsedSendPasswordReset !== undefined) {
            formData.append("sendPasswordReset", String(parsedSendPasswordReset));
        }
        // Empty string = reset to the PriceMe Admin user (multipart cannot carry null)
        if (payload.creatorUserId !== undefined) formData.append("creatorUserId", payload.creatorUserId ?? "");
        if (payload.postBody !== undefined) formData.append("body", payload.postBody ?? "");
        if (payload.longLink) formData.append("longLink", payload.longLink);
        if (formattedListingAgent && Object.keys(formattedListingAgent).length > 0) {
            formData.append("listingAgent", JSON.stringify(formattedListingAgent));
        }
        if (formattedPropertyData && Object.keys(formattedPropertyData).length > 0) {
            formData.append("propertyData", JSON.stringify(formattedPropertyData));
        }
        if (images?.length) {
            images.forEach((file) => formData.append("images", file));
        }
        if (agentImage) {
            formData.append("agentImage", agentImage);
        }

        return await axiosInstance.patch(`/api/playable-properties/${id}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    }

    const body: Record<string, unknown> = {
        ...(payload.playDate && { playDate: payload.playDate }),
        ...(payload.mlsId && { listingId: payload.mlsId }),
        ...(payload.url && { url: payload.url }),
        ...(parsedImageIndex !== undefined && { imageIndex: parsedImageIndex }),
        ...(payload.emailPuzzleSubject && { emailPuzzleSubject: payload.emailPuzzleSubject }),
        ...(parsedEmailImage !== undefined && { emailPuzzleImage: parsedEmailImage }),
        ...(payload.emailPuzzleBody && { emailPuzzleBody: payload.emailPuzzleBody }),
        ...(parsedFeatureAgent !== undefined && { featureAgent: parsedFeatureAgent }),
        ...(parsedSendDailyEmail !== undefined && { sendDailyEmail: parsedSendDailyEmail }),
        ...(parsedSendClaimAccountLink !== undefined && { sendClaimAccountLink: parsedSendClaimAccountLink }),
        ...(parsedSendPasswordReset !== undefined && { sendPasswordReset: parsedSendPasswordReset }),
        ...(payload.longLink && { longLink: payload.longLink }),
        ...(payload.creatorUserId !== undefined && { creatorUserId: payload.creatorUserId }),
        ...(payload.postBody !== undefined && { body: payload.postBody }),
        ...(formattedListingAgent && { listingAgent: formattedListingAgent }),
        ...(formattedPropertyData && { propertyData: formattedPropertyData }),
    };

    return await axiosInstance.patch(`/api/playable-properties/${id}`, body);
};

/**
 * Gets the next unplayed playable property for a user
 * @param userId - The user ID
 * @param excludePlayablePropertyId - Optional playable property ID to exclude (typically the current one)
 * @returns The next unplayed playable property with minimal property data
 */
export const getNextPlayableProperty = async (userId: UUID, excludePlayablePropertyId?: string | number | null) => {
    const params = excludePlayablePropertyId ? { excludePlayablePropertyId } : {};
    return await axios.get(`/api/playable-properties/next/${userId}`, { params });
};

/**
 * Gets a specific image URL for a property by index
 * @param propertyId - The property ID
 * @param imageIndex - The index of the image (0-based)
 * @returns The image URL string
 */
export const getPropertyImage = async (propertyId: UUID, imageIndex: number) => {
    return await axios.get(`/api/properties/${propertyId}/images/${imageIndex}`);
};

/**
 * Deletes a specific image from a property by index
 * @param propertyId - The property ID
 * @param imageIndex - The index of the image to delete (0-based)
 * @returns The axios response
 */
export const deletePropertyImage = async (propertyId: UUID, imageIndex: number) => {
    return await axiosInstance.delete(`/api/properties/${propertyId}/images/${imageIndex}`);
};

/**
 * Fetches puzzle scoreboard data for a specific playable property.
 * Returns paginated user guesses with scores and guess histories.
 *
 * @param {Object} params - Query parameters for the scoreboard request.
 * @param {string} params.playablePropertyId - The playable property ID to fetch scoreboard for.
 * @param {number} [params.page=1] - Page number for pagination (1-based).
 * @param {number} [params.limit=10] - Number of results per page.
 * @param {string} [params.currentUserId] - Registered user UUID for `currentUserScore` and list exclusion.
 * @param {number[]} [params.guestGuesses] - Guest guesses for provisional `currentUserScore` (max 6).
 * @returns {Promise<any>} Paginated scoreboard data with user guesses.
 */
export const getPuzzleScoreboard = async ({
    playablePropertyId,
    page = 1,
    limit = 10,
    currentUserId,
    guestGuesses,
}: {
    playablePropertyId: string;
    page?: number;
    limit?: number;
    currentUserId?: string;
    guestGuesses?: number[];
}) => {
    const query = new URLSearchParams({
        playableProperty: playablePropertyId,
        page: String(page),
        limit: String(limit),
    });
    if (currentUserId) {
        query.set("currentUserId", currentUserId);
    }
    if (guestGuesses?.length) {
        guestGuesses.slice(0, 6).forEach((guess) => {
            query.append("guestGuesses", String(guess));
        });
    }
    return await axios.get(`/api/user-guesses/scoreboard/puzzle?${query.toString()}`);
};

/**
 * Fetches comprehensive puzzle statistics for a playable property.
 * Alias for getPropertyPuzzleStats - returns the same complete dataset.
 *
 * @param {string} playablePropertyId - The playable property ID to fetch stats for
 * @returns {Promise<PuzzleStatsResponse>} Complete puzzle statistics including community metrics and engagement data
 */
export const getPuzzleCommunityStats = async (playablePropertyId: string): Promise<PuzzleStatsResponse> => {
    const { data } = await axios.get(`/api/properties/${playablePropertyId}/puzzle/stats`);
    return data;
};

export interface Agent {
    id: UUID;
    firstName: string;
    lastName: string;
    email?: string | null;
    phoneNumber?: string | null;
    brokerage?: string | null;
    link?: string | null;
    img?: string | null;
    cta?: string | null;
    instagram?: string | null;
    tiktok?: string | null;
    xTwitter?: string | null;
    facebook?: string | null;
    youtube?: string | null;
    mlsId?: string | null;
    auth0UserId?: string | null;
}

/**
 * Fetches agents from the database with optional search and limit.
 * Returns a list of agents with their details.
 *
 * @param {Object} params - Search parameters.
 * @param {string} [params.search] - Search term to filter by.
 * @param {number} [params.limit=10] - Maximum number of results to return.
 * @returns {Promise<Agent[]>} List of agents.
 */
export const getAgents = async ({ search, limit = 10 }: { search?: string; limit?: number } = {}): Promise<Agent[]> => {
    const query = new URLSearchParams({
        ...(search && { search }),
        limit: String(limit),
    }).toString();
    const { data } = await axios.get(`/api/agents?${query}`);
    return data;
};

/**
 * Searches for agent users with typeahead filtering.
 * Returns a list of users with type=agent filtered by search term.
 *
 * @param {Object} params - Search parameters.
 * @param {string} [params.search] - Search term to filter by (minimum 2 characters).
 * @param {number} [params.limit=10] - Maximum number of results to return.
 * @returns {Promise<any>} List of matching agent users.
 */
export const getUserAgents = async ({ search, limit = 10 }: { search?: string; limit?: number } = {}) => {
    const query = new URLSearchParams({
        ...(search && { search }),
        limit: String(limit),
    }).toString();
    const { data } = await axios.get(`/api/users/agents?${query}`);
    return data;
};

/**
 * Fetches properties for a specific agent profile with pagination.
 * Returns a list of properties associated with the agent.
 *
 * @param {string} agentNickname - The agent's nickname/identifier.
 * @param {Object} params - Pagination parameters.
 * @param {number} [params.page=1] - Page number for pagination (1-based).
 * @param {number} [params.limit=10] - Number of results per page.
 * @param {string} [params.requestingUserId] - The requesting user's ID.
 * @returns {Promise<any>} Paginated list of agent properties.
 */
export const getAgentProfileProperties = async (
    agentNickname: string,
    { page = 1, limit = 10, requestingUserId }: { page?: number; limit?: number; requestingUserId?: string } = {},
) => {
    const encodedAgentNickname = encodeURIComponent(agentNickname);
    const query = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        ...(requestingUserId && { requestingUserId }),
    }).toString();
    const { data } = await axios.get(`/api/agents/${encodedAgentNickname}/profile/properties?${query}`);
    return data;
};

/**
 * Response type for playable property comments API
 */
export interface PlayablePropertyCommentsResponse {
    data: Array<{
        id: string | null;
        userId: string;
        username: string;
        profilePhoto?: string | null;
        showScoreLeaderboard?: boolean;
        createdAt: string | null;
        body: string | null;
        guesses: number[];
        replies: Array<{
            id: string | null;
            userId: string;
            username: string;
            profilePhoto?: string | null;
            showScoreLeaderboard?: boolean;
            createdAt: string | null;
            body: string | null;
            guesses: number[];
            replies: Array<{
                id: string | null;
                userId: string;
                username: string;
                profilePhoto?: string | null;
                showScoreLeaderboard?: boolean;
                createdAt: string | null;
                body: string | null;
                guesses: number[];
                replies: any[];
            }> | null;
        }> | null;
    }>;
    guess_count: number;
    replies_count: number;
}

/**
 * Fetches comments for a specific playable property.
 * Returns a list of comments associated with the property.
 *
 * @param {string} propertyId - The playable property ID to fetch comments for.
 * @param {Object} options - Query options.
 * @param {string[]} [options.include] - Array of fields to include (e.g., ['guesses', 'replies']).
 * @returns {Promise<PlayablePropertyCommentsResponse>} Comments data with optional guess/reply counts.
 */
export const getPlayablePropertyComments = async (
    propertyId: string,
    { include, requestingUserId }: { include?: string[]; requestingUserId?: string } = {},
): Promise<PlayablePropertyCommentsResponse> => {
    const queryParams = new URLSearchParams();
    if (include?.length) {
        queryParams.set("include", include.join(","));
    }
    if (requestingUserId) {
        queryParams.set("requestingUserId", requestingUserId);
    }
    const query = queryParams.toString();

    const { data } = await axios.get(`/api/playable-properties/${propertyId}/comments${query ? `?${query}` : ""}`);
    return data;
};

/**
 * Deletes a comment from a specific playable property.
 * Requires authentication and user must be the comment author.
 *
 * @param {string} propertyId - The playable property ID.
 * @param {string} commentId - The comment ID to delete.
 * @param {string} userId - The user ID of the comment author.
 * @returns {Promise<any>} Delete response.
 */
export const deletePlayablePropertyComment = async (propertyId: string, commentId: string, userId: string) => {
    const { data } = await axiosInstance.delete(`/api/playable-properties/${propertyId}/comments`, {
        data: {
            commentId,
            userId,
        },
    });
    return data;
};

/**
 * Kicks off a background import of all approved pipeline listings (admin).
 * Returns immediately; imported listings appear on the schedule as they finish.
 * @returns {Promise<{success: boolean; message: string}>} Kickoff acknowledgement.
 */
export const importApprovedListings = async (): Promise<{ success: boolean; message: string }> => {
    return (await axiosInstance.post(`/api/importer/import-approved`)).data;
};

/**
 * Fetches recent pipeline import runs and unresolved import issues (admin).
 * @returns {Promise<{running: boolean; runs: any[]; openIssues: any[]}>} Importer status.
 */
export const getImporterRuns = async (): Promise<{ running: boolean; runs: any[]; openIssues: any[] }> => {
    return (await axiosInstance.get(`/api/importer/runs`)).data;
};
